<template>
  <div>
    <div class="flex flex-row">
      <div
        ref="currencyContainer"
        tabindex="1"
        class="relative"
        @focus="showOptions"
        @blur="handleBlur"
      >
        <div
          :class="[disabled
            ? 'bg-disabled-currency-container cursor-not-allowed'
            : 'bg-currency-container cursor-pointer']"
        >
          <p class="w-2/3 font-semibold text-white">
            {{ getText() }}
          </p>
          <div class="w-1/3 flex flex-row items-center justify-end">
            <vs-icon
              color="#FFFFFF"
              :icon="isOptionShown ? 'arrow_drop_up' : 'arrow_drop_down'"
            />
          </div>
        </div>
        <div
          v-if="isOptionShown"
          class="bg-option-container"
        >
          <ul>
            <li
              v-for="(item, index) of options"
              class="my-1"
              :key="index"
              :class="{'active': isOptionSelected(item)}"
              @click.stop="selectValue(item)"
            >
              <span>{{ item.text }}</span>
            </li>
          </ul>
        </div>
      </div>
      <vs-row
        v-if="currency === 'idr'"
        vs-w="12"
      >
        <vs-col vs-w="12">
          <v-autonumeric
            ref="nominalContainer"
            v-model="nominal"
            class="nominal-primary"
            :options="numericOptions"
            :disabled="disabled"
          />
        </vs-col>
      </vs-row>
      <vs-row
        v-else
        vs-w="12"
      >
        <vs-col
          vs-w="6"
          class="pr-2"
        >
          <v-autonumeric
            ref="nominalContainer"
            v-model="nominal"
            class="nominal-primary"
            :options="numericOptions"
            :placeholder="placeholder"
            :disabled="disabled"
          />
        </vs-col>
        <vs-col
          vs-w="6"
          class="pl-2"
        >
          <p class="mb-3 mt-neg-3 font-semibold">
            Currency
          </p>
          <v-autonumeric
            disabled
            v-model="rate"
            class="autonumeric-primary"
            :options="numericOptions"
            :placeholder="placeholder"
          />
        </vs-col>
      </vs-row>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    /*
    * `value` should have `value`, 'rate` and 'currency` keys
    * ex : { value: 0, rate: 0, currency: 'idr' }
    * */
    value: {
      type: null,
      default: () => null
    },
    placeholder: {
      type: String,
      default: () => null
    },
    options: {
      type: Array,
      default: () => []
    },
    rateOption: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: () => false
    },
    isValid: {
      type: Boolean,
      default: () => true
    }
  },
  data() {
    return {
      isOptionShown: false,
      currency: this.value.currency,
      rate: this.value.rate,
      nominal: this.value.value,
      numericOptions: {
        allowDecimalPadding: false,
        digitGroupSeparator: '.',
        decimalCharacter: ',',
        decimalCharacterAlternative: '.',
        roundingMethod: 'U',
        minimumValue: '0',
        modifyValueOnWheel: false
      }
    }
  },
  watch: {
    value: {
      handler(val) {
        if (val) {
          this.currency = val.currency
          this.rate = val.rate
          this.nominal = val.value

          this.numericOptions = {
            ...this.numericOptions,
            digitGroupSeparator: this.currency === 'idr' ? '.' : ',',
            decimalCharacter: this.currency === 'idr' ? ',' : '.'
          }
        }
      }
    },
    nominal: {
      handler(val) {
        const model = {
          currency: this.currency,
          rate: this.rate,
          value: val
        }
        this.$emit('input', model)
      }
    },
    rate: {
      handler(val) {
        const model = {
          currency: this.currency,
          rate: val,
          value: this.nominal
        }
        this.$emit('input', model)
      }
    },
    isValid: {
      handler(val) {
        if (!this.disabled) {
          if (val) {
            this.$refs.nominalContainer.$el.classList.remove('nominal-danger')
            this.$refs.nominalContainer.$el.classList.add('nominal-primary')
            return
          }

          this.$refs.nominalContainer.$el.classList.remove('nominal-primary')
          this.$refs.nominalContainer.$el.classList.add('nominal-danger')
          return
        }

        this.$refs.nominalContainer.$el.classList.add('nominal-primary')
      }
    }
  },
  methods: {
    showOptions() {
      if (!this.disabled && !this.isOptionShown) {
        this.isOptionShown = true

        this.$refs.currencyContainer.focus()
      }
    },
    closeOptions() {
      if (!this.disabled && this.isOptionShown) {
        this.isOptionShown = false

        this.$refs.currencyContainer.blur()
      }
    },
    handleBlur(ev) {
      if (!ev.currentTarget.contains(ev.relatedTarget)) {
        this.closeOptions()
      }
    },
    getText() {
      if (this.options.length) {
        return this.options.find(item => this.currency === item.value).text
      }
    },
    selectValue(item) {
      this.currency = item.value
      this.rate = this.rateOption.find(rate => rate.value === item.value).rate
      const model = {
        currency: this.currency,
        rate: this.rate,
        value: this.nominal
      }
      this.numericOptions = {
        ...this.numericOptions,
        digitGroupSeparator: this.currency === 'idr' ? '.' : ',',
        decimalCharacter: this.currency === 'idr' ? ',' : '.'
      }
      this.$emit('input', model)

      this.closeOptions()
    },
    isOptionSelected(item) {
      return item.value === this.currency
    }
  }
}
</script>

<style lang="scss" scoped>
li {
  @apply p-3 rounded-lg cursor-pointer;

  color: #3D3D3D;

  &:hover {
    @apply font-semibold;

    background-color: #F1F1FD;
    color: #493493;
  }
}

.bg-currency-container {
  @apply pl-3 flex flex-row items-center rounded-tl-lg rounded-bl-lg bg-primary;

  height: calc(1rem * 10 / 3);
}

.bg-disabled-currency-container {
  @apply pl-3 flex flex-row items-center rounded-tl-lg rounded-bl-lg bg-grey-001;

  height: calc(1rem * 10 / 3);
}

.bg-option-container {
  @apply p-3 mt-1 w-full flex flex-col absolute bg-white rounded-lg shadow-lg;

  min-width: 125px;
  max-height: 20rem;
  z-index: 1000;
}

.active {
  @apply font-semibold;

  background-color: #F1F1FD;
  color: #6546C3;
}

.mt-neg-3 {
  margin-top: -1.95rem;
}

.nominal-primary {
  @apply w-full;
  @apply relative;
  @apply inline-block;
  @apply rounded-tr-lg rounded-br-lg;
  @apply border-transparent;
  @apply outline-none;
  @apply shadow-none;
  @apply py-85;
  @apply px-4;
  @apply text-base;
  @apply font-normal;
  height: calc( 1rem * 10 / 3 );
  background-color: #F1F1FD;

  &::-webkit-input-placeholder { /* Edge */
    @apply rounded-lg;
    @apply p-0;
  }

  &:-ms-input-placeholder { /* Internet Explorer 10-11 */
    @apply rounded-lg;
    @apply p-0;
  }

  &::placeholder {
    @apply rounded-lg;
    @apply p-0;
  }

  &:disabled {
    background-color: #ECEBED;
  }

  &:disabled,
  &:disabled+.vs-input--placeholder {
    @apply text-black;
    @apply opacity-100;
  }

  &:focus {
    @apply border-2 border-secondary;
  }
}

.nominal-danger {
  @extend .nominal-primary;
  @apply border-2 border-danger #{!important};
}
</style>
